import React, { useCallback, useEffect, useState } from 'react'
import { View, Text as RNText, TextStyle, Pressable } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import { Instance } from 'mobx-state-tree'

import { useStores, setPageTitle, FilterTermsProps } from '@trivie/core'
import { color } from '@trivie/ui-web/theme'
import { TopicCard } from '@trivie/ui-mobile'
import { UserPath } from '@trivie/core/src/models/workflow/Path'

export interface PathsTabProps {
  searchTerm?: string
  filterTerms?: FilterTermsProps
  clearFilters: () => void
}

export const PathsTab = observer((props: PathsTabProps) => {
  const { pathStore } = useStores()
  const { userPaths } = pathStore
  const { clearFilters, searchTerm, filterTerms } = props

  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })

  const history = useHistory()

  const goToPath = (path: Instance<typeof UserPath>) => {
    setPageTitle(`${path.name} - ${process.env.REACT_APP_SITE_TITLE}`)
    if (path.subscription_user_id) {
      history.push(
        `/library/path/?subscription_user_id=${path.subscription_user_id}&path_id=${path.path_id}`,
      )
    } else {
      history.push(`/library/path/?assignment_id=${path.assignment_id}&path_id=${path.path_id}`)
    }
  }

  function useWindowSize() {
    useEffect(() => {
      function handleResize() {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        })
      }
      window.addEventListener('resize', handleResize)
      handleResize()
      return () => window.removeEventListener('resize', handleResize)
    }, [])
    return windowSize
  }

  useWindowSize()
  function responsiveGrid() {
    if (windowSize.width < 1024) {
      return THINGRID
    } else if (windowSize.width < 1440) {
      return NORMALGRID
    } else {
      return WIDEGRID
    }
  }

  const getData = useCallback(() => {
    const fullData = userPaths
      .slice()
      ?.filter(
        (f) => !searchTerm || f.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
      )

    if (!filterTerms?.status || filterTerms.status.length === 0) return fullData

    let result: typeof fullData = []

    if (filterTerms?.status.includes('1')) {
      result = result.concat(
        fullData.filter((f) => {
          const isCompleted = f.nodes_completed === f.node_count
          return f?.nodes_completed === 0 && !isCompleted
        }),
      )
    }

    //in progress
    if (filterTerms?.status.includes('2')) {
      result = result.concat(
        fullData.filter((f) => f.nodes_completed > 0 && f.nodes_completed < f.node_count),
      )
    }

    //completed
    if (filterTerms?.status.includes('3')) {
      result = result.concat(fullData.filter((f) => f.nodes_completed === f.node_count))
    }
    return result
  }, [userPaths, filterTerms, searchTerm])

  const filteredData = getData()

  return (
    <div style={CONTENT}>
      <div style={responsiveGrid()}>
        {filteredData.map((path: Instance<typeof UserPath>, index: number) => (
          <TopicCard
            // @ts-ignore
            dataSet={{ qat: `path-card-${index}` }}
            path={path}
            key={`${path.name}_${index}`}
            onPress={() => goToPath(path)}
          />
        ))}
      </div>
      {filteredData?.length !== userPaths.length ? (
        <View style={{ paddingHorizontal: 40, paddingTop: filteredData?.length === 0 ? 120 : 12 }}>
          <RNText style={FILTER}>
            {`${
              userPaths.length - filteredData?.length
            } other paths are not shown due to your active filters.`}
            <Pressable onPress={() => clearFilters()}>
              <RNText style={CLEAR_FILTER}>{'Clear Filters?'}</RNText>
            </Pressable>
          </RNText>
        </View>
      ) : null}
    </div>
  )
})

/**
 * Styling
 */
const CONTENT: React.CSSProperties = {
  backgroundColor: color.white,
  height: '100%',
}
const WIDEGRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: '1fr',
  gridGap: 20,
  paddingTop: 0,
  paddingBottom: 20,
}
const NORMALGRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: '1fr',
  gridGap: 20,
  paddingTop: 0,
  paddingBottom: 20,
}
const THINGRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: '1fr',
  gridGap: 20,
  paddingTop: 0,
  paddingBottom: 20,
}
const FILTER: TextStyle = {
  alignSelf: 'center',
  textAlign: 'center',
  fontStyle: 'italic',
  color: color.shade50,
}
const CLEAR_FILTER: TextStyle = {
  ...FILTER,
  alignSelf: 'center',
  paddingTop: 40,
  paddingLeft: 4,
  textDecorationLine: 'underline',
}
